import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSiteContext } from '../context/SiteContext.jsx';
import TemplateRenderer from './TemplateRenderer.jsx';
import { DomainError, DomainLoading } from '../domains/config/domainFallbacks.jsx';
import { resolveActiveTemplate } from '../api/action/template.jsx';
import {
  findTemplatePage,
  getDefaultTemplateForDomain,
  normalizeTemplateForReader,
  setStoredActiveTemplate
} from './TemplateStorage.jsx';

const templateCompanyIdOf = (template) => (
  template?.company?.primaryId
  || template?.companyId
  || template?.attributes?.companyId
);

const templateDomainTypeOf = (template) => (
  template?.domain?.type
  || template?.attributes?.sourceDomain
  || ''
);

const readCurrentContext = (providedDomainType, siteContext) => ({
  companyId: siteContext?.companyId
    || (typeof window !== 'undefined' ? localStorage.getItem('CompPrimaryId') : ''),
  domainType: siteContext?.domainType
    || providedDomainType
    || (typeof window !== 'undefined' ? localStorage.getItem('domainType') : '')
});

const persistResolvedTemplateContext = (template, fallbackDomainType) => {
  try {
    const templateCompanyId = templateCompanyIdOf(template);
    const templateDomainType = templateDomainTypeOf(template) || fallbackDomainType;
    if (templateCompanyId) localStorage.setItem('CompPrimaryId', String(templateCompanyId));
    if (templateDomainType) localStorage.setItem('domainType', String(templateDomainType));
  } catch {
  }
};

const templateSnapshot = (template) => {
  try {
    return JSON.stringify(template);
  } catch {
    return '';
  }
};

const DynamicTemplatePage = ({
  pageKey = 'home',
  route,
  domainType: providedDomainType,
  siteContext: providedSiteContext,
  resolvedTemplate = null,
  fallbackElement = null,
  fallbackComponent: FallbackComponent = null
}) => {
  const inheritedSiteContext = useSiteContext();
  const siteContext = providedSiteContext || inheritedSiteContext;
  const { companyId: storedCompanyId, domainType: storedDomainType } = readCurrentContext(providedDomainType, siteContext);
  const contextResolutionStatus = siteContext?.templateResolution?.status;
  const initialResolutionStatus = contextResolutionStatus || (resolvedTemplate ? 'assigned' : 'pending');
  const initialStoredTemplate = siteContext
    ? (contextResolutionStatus === 'assigned' ? siteContext.activeTemplate : null)
    : resolvedTemplate;
  const [activeTemplate, setActiveTemplate] = useState(initialStoredTemplate || null);
  const [resolutionStatus, setResolutionStatus] = useState(initialResolutionStatus);
  const [loading, setLoading] = useState(initialResolutionStatus === 'pending');
  const requestRef = useRef(0);
  const setStableActiveTemplate = useCallback((template) => {
    setActiveTemplate((currentTemplate) => (
      templateSnapshot(currentTemplate) === templateSnapshot(template) ? currentTemplate : template
    ));
  }, []);
  const renderFallbackContent = () => {
    if (fallbackElement) {
      return fallbackElement;
    }
    if (FallbackComponent) {
      return <FallbackComponent />;
    }
    return <DomainLoading />;
  };
  useEffect(() => {
    const refreshFromBrowserStorage = () => {
      if (siteContext) return;
      // Only external browser storage changes should clear this reader. Do not listen to
      // avps-template-updated here: setStoredActiveTemplate dispatches that event during
      // this component's own successful load, which was resetting it back to loading.
      requestRef.current += 1;
      setStableActiveTemplate(null);
      setLoading(true);
    };
    window.addEventListener('storage', refreshFromBrowserStorage);
    return () => {
      window.removeEventListener('storage', refreshFromBrowserStorage);
    };
  }, [siteContext, setStableActiveTemplate]);

  useEffect(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    let cancelled = false;

    if (siteContext) {
      const status = siteContext.templateResolution?.status || 'pending';
      setResolutionStatus(status);
      setStableActiveTemplate(status === 'assigned' ? siteContext.activeTemplate : null);
      setLoading(status === 'pending');
      return () => {
        cancelled = true;
      };
    }

    setStableActiveTemplate(resolvedTemplate || null);
    setResolutionStatus(resolvedTemplate ? 'assigned' : 'pending');
    setLoading(!resolvedTemplate);

    resolveActiveTemplate(storedCompanyId, storedDomainType, { forceRefresh: true })
      .then((resolution) => {
        if (cancelled || requestRef.current !== requestId) return;

        setResolutionStatus(resolution.status);
        setStableActiveTemplate(resolution.status === 'assigned' ? resolution.template : null);
        if (resolution.status === 'assigned') {
          persistResolvedTemplateContext(resolution.template, storedDomainType);
          setStoredActiveTemplate(resolution.template, { forceStore: true });
        } else if (resolution.status === 'default') {
          setStoredActiveTemplate(null, { forceStore: true });
        }
      })
      .catch(() => {
        if (cancelled || requestRef.current !== requestId) return;
        setResolutionStatus('error');
        setStableActiveTemplate(null);
      })
      .finally(() => {
        if (!cancelled && requestRef.current === requestId) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedTemplate, siteContext, storedCompanyId, storedDomainType, setStableActiveTemplate]);

  // Block only while the request is genuinely in flight.
  if (loading || resolutionStatus === 'pending') {
    return <DomainLoading />;
  }

  if (resolutionStatus === 'error') {
    return <DomainError />;
  }

  if (resolutionStatus === 'default' && !activeTemplate && fallbackElement) {
    return fallbackElement;
  }

  if (resolutionStatus === 'default' && !activeTemplate && FallbackComponent) {
    return <FallbackComponent />;
  }

  const sourceTemplate = resolutionStatus === 'default'
    ? getDefaultTemplateForDomain(storedDomainType || providedDomainType || 'EDUCATION')
    : activeTemplate;

  if (!sourceTemplate) {
    return renderFallbackContent();
  }

  const resolvedDomainType = templateDomainTypeOf(sourceTemplate) || storedDomainType;
  const template = normalizeTemplateForReader(sourceTemplate, resolvedDomainType);
  const page = findTemplatePage(template, pageKey, route);

  if (!page) {
    // A configured template owns its route tree. Do not silently substitute a
    // built-in/default page when the assigned template does not define a route.
    if (resolutionStatus === 'default' && fallbackElement) {
      return fallbackElement;
    }
    if (resolutionStatus === 'default' && FallbackComponent) {
      return <FallbackComponent />;
    }
    if (resolutionStatus === 'assigned') {
      return <DomainError />;
    }
    return <Navigate to="/" replace />;
  }

  return <TemplateRenderer template={template} page={page} fallback={renderFallbackContent()} />;
};

export default DynamicTemplatePage;
